import React, { useState } from 'react';
import { Divider, makeStyles, Typography } from '@material-ui/core';
import {
    SaveButton,
    SimpleForm,
    TextInput,
    Toolbar,
    required,
    useAuthenticated,
    useDataProvider,
    useNotify,
    usePermissions,
} from 'react-admin';

import {
    CellphoneInput,
    CpfInput,
    Loader,
    Title,
    Wrapper,
} from '../../components';
import { PERMISSIONS } from '../../constants';
import { getChainId } from '../../lib';
import { validateCellphone, validateForm } from '../../utils/customerCreate';

const useStyles = makeStyles(theme => ({
    form: {
        maxWidth: 420,
    },
    sectionTitle: {
        marginTop: theme.spacing(2),
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    divider: {
        marginTop: theme.spacing(1),
    },
    denied: {
        marginTop: theme.spacing(2),
    },
}));

/**
 * Título de seção do formulário.
 *
 * Desestrutura só `children` de propósito: o `FormInput` do react-admin clona
 * todo filho do `SimpleForm` injetando `basePath`/`record`/`resource`/`variant`,
 * e repassar isso para um elemento do DOM gera warning de prop desconhecida.
 */
const SectionTitle = ({ children }) => {
    const classes = useStyles();
    return (
        <div>
            <Typography
                variant="subtitle2"
                color="textSecondary"
                className={classes.sectionTitle}>
                {children}
            </Typography>
            <Divider className={classes.divider} />
        </div>
    );
};

const CustomerCreateToolbar = props => (
    <Toolbar {...props}>
        <SaveButton label="Cadastrar" />
    </Toolbar>
);

const CustomerCreateForm = () => {
    const classes = useStyles();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [saving, setSaving] = useState(false);
    // Trocar a `version` remonta o `<Form>` do final-form (FormWithRedirect passa
    // `key={version}`) — é como se limpa o formulário para o próximo cadastro.
    const [version, setVersion] = useState(0);

    const handleSave = values => {
        const { name, cpf, cellphone, password, confirmPassword, chainId } = values;
        setSaving(true);

        // Mesmo payload do pontuax-admin (`ClientCreate` + `store/providers/create`):
        // POST /customers com CPF/celular só com dígitos. O Joi do backend
        // (createCustomerModelChain) recusa qualquer campo extra.
        return dataProvider
            .create('customers', {
                data: { name: name.trim(), cpf, cellphone, password, confirmPassword, chainId },
            })
            .then(() => {
                notify('Cliente cadastrado com sucesso!');
                setVersion(v => v + 1);
            })
            .catch(err => {
                notify(err.message || 'Erro ao cadastrar cliente.', 'warning');
            })
            .finally(() => { setSaving(false); });
    };

    return (
        <Wrapper>
            <Title>Cadastrar novo cliente</Title>

            <div className={classes.form}>
                <SimpleForm
                    version={version}
                    save={handleSave}
                    saving={saving}
                    validate={validateForm}
                    initialValues={{ chainId: getChainId() }}
                    variant="outlined"
                    submitOnEnter={false}
                    toolbar={<CustomerCreateToolbar />}>

                    <SectionTitle>Dados pessoais</SectionTitle>

                    <TextInput
                        source="name"
                        label="Nome"
                        options={{ autoComplete: 'off' }}
                        validate={required()}
                        fullWidth
                    />
                    <CpfInput
                        source="cpf"
                        label="CPF"
                        options={{ autoComplete: 'off' }}
                        validate={required('CPF é obrigatório')}
                        fullWidth
                    />
                    <CellphoneInput
                        source="cellphone"
                        label="Celular"
                        options={{ autoComplete: 'off' }}
                        validate={validateCellphone}
                        fullWidth
                    />

                    <SectionTitle>Acesso</SectionTitle>

                    <TextInput
                        source="password"
                        label="Senha"
                        options={{ autoComplete: 'new-password' }}
                        validate={required()}
                        fullWidth
                    />
                    <TextInput
                        source="confirmPassword"
                        label="Confirmar senha"
                        options={{ autoComplete: 'new-password' }}
                        validate={required()}
                        fullWidth
                    />
                </SimpleForm>
            </div>
        </Wrapper>
    );
};

/**
 * Cadastro de cliente no balcão, espelhando o `ClientCreate` do pontuax-admin.
 *
 * Diferente do menu "Cadastro" (`containers/Register`), que é recuperação de
 * senha / confirmação por SMS do cliente que já existe ou se auto-cadastra:
 * aqui o operador cria o cliente direto, com senha definida na hora.
 *
 * Gate na permission `create-customer` — a mesma do admin e a mesma exigida no
 * scope do JWT por `POST /customers` no backend. O menu já esconde o item, mas
 * a rota é pública dentro do app (dá para digitar a URL), então repete aqui.
 */
const CustomerCreate = () => {
    useAuthenticated();
    const classes = useStyles();
    const { loading, permissions } = usePermissions();

    if (loading) {
        return (
            <Wrapper>
                <Loader />
            </Wrapper>
        );
    }

    if (!(permissions || []).includes(PERMISSIONS.CREATE_CUSTOMER)) {
        return (
            <Wrapper>
                <Title>Cadastrar novo cliente</Title>
                <Typography className={classes.denied}>
                    Você não tem permissão para cadastrar clientes.
                </Typography>
            </Wrapper>
        );
    }

    return <CustomerCreateForm />;
};

export default CustomerCreate;
